<template>
  <div class="inspection-wrapper flex-container">
    <div class="checkbox-groups">
      <div
        v-for="group in dynamicGroups"
        :key="group.key"
        class="checkbox-group"
      >
        <p class="group-title">{{ group.title }}</p>
        <div
          v-for="item in group.items"
          :key="item.fieldKey"
          class="inspection-item"
          :class="{
            'inspection-item--error': showErrors && item.hasError,
            'inspection-item--clickable': item.type === 'checkbox',
            'inspection-item--radio': item.type === 'radio',
            'inspection-item--derived-checkbox':
              item.type === 'derived-checkbox',
          }"
          @click="onInspectionItemClick(group.key, item)"
        >
          <template v-if="item.type === 'derived-checkbox'">
            <v-checkbox
              :input-value="item.value"
              :label="item.title"
              color="#9B004B"
              class="inspection-checkbox inspection-checkbox--readonly mt-0 pt-0"
              :class="{ 'inspection-checkbox--readonly-checked': item.value }"
              hide-details
              disabled
              @click.stop.prevent
            />
            <div class="inspection-subitems">
              <v-checkbox
                v-for="child in item.children"
                :key="child.fieldKey"
                :input-value="child.value"
                :label="child.title"
                color="#9B004B"
                class="inspection-checkbox inspection-checkbox--nested mt-0 pt-0"
                hide-details
                @click.stop
                @change="setInspectionField(group.key, child.fieldKey, $event)"
              />
            </div>
          </template>
          <template v-else-if="item.type === 'radio'">
            <div class="inspection-item-label">{{ item.title }}</div>
            <v-radio-group
              :value="item.value"
              class="inspection-radio-group mt-2"
              hide-details
              row
              @change="setInspectionField(group.key, item.fieldKey, $event)"
            >
              <v-radio
                v-for="option in item.options"
                :key="option.value"
                :label="option.label"
                :value="option.value"
                color="#9B004B"
              />
            </v-radio-group>
          </template>
          <template v-else>
            <v-checkbox
              :input-value="item.value"
              :label="item.title"
              color="#9B004B"
              class="inspection-checkbox mt-0 pt-0"
              hide-details
              @click.stop
              @change="setInspectionField(group.key, item.fieldKey, $event)"
            />
          </template>
        </div>
        <p v-if="group.showHeatPumpHint" class="inspection-hint mb-0">
          {{
            $t('consumption_devices_details.heat_pump_connection_value_hint')
          }}
        </p>
      </div>
      <div class="supply-checkbox-row">
        <div
          class="inspection-item inspection-item--clickable"
          :class="{
            'inspection-item--error':
              showErrors && !supplyInstallationNumberProvided,
          }"
          @click="toggleSupplyInstallationNumberProvided"
        >
          <v-checkbox
            :input-value="supplyInstallationNumberProvided"
            :label="
              $t(
                'consumption_devices_details.supply_installation_number_provided'
              )
            "
            color="#9B004B"
            class="inspection-checkbox mt-0 pt-0"
            hide-details
            @click.stop
            @change="setSupplyInstallationNumberProvided"
          />
        </div>
      </div>
    </div>

    <div class="error-dialog" style="margin-top: 10px" v-if="showErrors">
      {{ $t('consumption_devices_details.inspection_validation_error') }}
    </div>
    <div
      class="error-dialog"
      style="margin-top: 10px"
      v-if="showSupplyNumberMissingError"
    >
      {{ $t('consumption_devices_details.supply_number_missing_error') }}
    </div>

    <div class="flex-container-nested border-radius-bottom">
      <div class="buttons-label">
        <div v-if="showLoading" class="d-flex align-center justify-center">
          <v-progress-circular
            indeterminate
            color="primary"
            class="mr-4"
          ></v-progress-circular>
        </div>
        <div v-else>
          <button
            type="button"
            class="btn-confirm-inspection"
            @click="confirmConsumptionDevicesInspection"
            :disabled="showLoading"
          >
            <div class="btn-text" style="width: inherit; color: white">
              Prüfung bestätigen
            </div>
          </button>
          <button
            type="button"
            class="btn-cancel-inspection"
            @click="declineInspection"
          >
            <div class="btn-text" style="width: inherit">Prüfung ablehnen</div>
          </button>
          <button
            type="button"
            class="btn-save-current-state"
            @click="updateConsumptionDevicesInspectionValues(false)"
          >
            Zwischenstand speichern
          </button>
          <v-alert
            class="alert_success"
            style="background-color: #e4f0d6 !important"
            :value="alert"
            max-width="270px"
            max-height="35px"
            text
            transition="scale-transition"
            v-if="
              !alertError &&
              lastUpdatedComponent === 'consumptionDevicesSaveCurrentResult'
            "
          >
            <template v-slot:append>
              <v-icon color="green" class="ml-2">mdi-check</v-icon>
            </template>
            Erfolgreich gespeichert
          </v-alert>
          <v-alert
            type="error"
            class="alert_error"
            :value="alert"
            max-width="270px"
            max-height="35px"
            v-if="
              alertError &&
              lastUpdatedComponent === 'consumptionDevicesSaveCurrentResult'
            "
            text
            transition="scale-transition"
          >
            Speicherung fehlgeschlagen
          </v-alert>
        </div>
      </div>
    </div>

    <NewModalDialog
      v-if="isModalVisible"
      :value="noteValue"
      role="dialog"
      @update-note-data="noteValue.note = $event"
      @cancel-data="closeModal"
      @save-data="closeModalAndSaveData"
    >
      <template v-slot:body>
        <div class="dialogTitle">Prüfung ablehnen</div>
      </template>
      <template v-slot:footer>
        <div class="footer_container">
          <div v-if="showDeclineError" class="error_message_dialog">
            Bitte Ablehnungsgrund eingeben.
          </div>
          <div class="button_container">
            <button
              type="button"
              class="btn-save"
              @click="closeModalAndSaveData"
              :disabled="showDeclineLoading"
            >
              <v-progress-circular
                indeterminate
                color="white"
                class="mr-4"
                v-if="showDeclineLoading"
              ></v-progress-circular>
              Prüfung ablehnen
            </button>
            <button type="button" class="btn-cancel" @click="closeModal">
              abbrechen
            </button>
            <v-alert
              type="error"
              class="alert_error mt-4"
              max-width="270px"
              v-if="
                lastUpdatedComponent === 'consumptionDevicesOrderRefused' &&
                alertError
              "
              text
              transition="scale-transition"
            >
              Ablehnen fehlgeschlagen
            </v-alert>
          </div>
        </div>
      </template>
    </NewModalDialog>
  </div>
</template>

<script>
import {
  updateConsumptionDeviceTechnicalInspectionData,
  updateConsumptionDevicesOrderStatus,
} from '@/services/ConsumptionDevicesService';
import { NewModalDialog } from '@newnetzportal/netzportal-webapp-storybook-components';
import noteService from '../../services/NoteService';
import { emptyEventData } from '../../store/ordersModule';

function cloneInspectionData(inspectionData) {
  return JSON.parse(JSON.stringify(inspectionData || {}));
}

const baseInspectionItems = [
  {
    fieldKey: 'commissioning_date_checked',
    titleKey: 'consumption_devices_details.commissioning_date_checked',
    type: 'checkbox',
  },
  {
    fieldKey: 'counter_number_checked',
    titleKey: 'consumption_devices_details.counter_number_checked',
    type: 'derived-checkbox',
    children: [
      {
        fieldKey: 'counter_number_matches_photo_checked',
        titleKey:
          'consumption_devices_details.counter_number_matches_photo_checked',
      },
      {
        fieldKey: 'counter_number_matches_location_checked',
        titleKey:
          'consumption_devices_details.counter_number_matches_location_checked',
      },
    ],
  },
  {
    fieldKey: 'billing_module_assignment_possible_checked',
    titleKey:
      'consumption_devices_details.billing_module_assignment_possible_checked',
    type: 'checkbox',
  },
  {
    fieldKey: 'manufacturer_type_plausible_checked',
    titleKey: 'consumption_devices_details.manufacturer_type_plausible_checked',
    type: 'checkbox',
  },
  {
    fieldKey: 'electrical_connection_value_plausible_checked',
    titleKey:
      'consumption_devices_details.electrical_connection_value_plausible_checked',
    heatPumpTitleKey:
      'consumption_devices_details.electrical_connection_value_plausible_heat_pump_checked',
    type: 'checkbox',
  },
  {
    fieldKey: 'ripple_receiver',
    titleKey: 'consumption_devices_details.ripple_receiver',
    type: 'radio',
    options: [
      { labelKey: 'consumption_devices.order_details.yes', value: 'yes' },
      { labelKey: 'consumption_devices.order_details.no', value: 'no' },
    ],
  },
];

const storageInspectionItem = {
  fieldKey: 'storage_capacity_plausible_checked',
  titleKey: 'consumption_devices_details.storage_capacity_plausible_checked',
  type: 'checkbox',
};

export default {
  name: 'ConsumptionDevicesInspectionComponent',
  components: {
    NewModalDialog,
  },
  props: {
    selectedConsumptionDevicesOrder: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      alert: false,
      showLoading: false,
      alertError: false,
      showErrors: false,
      showSupplyNumberMissingError: false,
      showDeclineError: false,
      showDeclineLoading: false,
      noteValue: { note: '' },
      isModalVisible: false,
      lastSavedInspectionData: {},
    };
  },
  computed: {
    dynamicGroups() {
      const devices =
        this.selectedConsumptionDevicesOrder?.controllable_devices || {};

      const deviceMap = {
        wallbox: this.$t('consumption_devices.wallbox'),
        heat_pump: this.$t('consumption_devices.heat_pump'),
        air_conditioning: this.$t('consumption_devices.air_conditioning'),
        battery_storage: this.$t('consumption_devices.battery_storage'),
      };

      return Object.keys(deviceMap)
        .filter((key) => devices[key])
        .map((key) => ({
          key,
          title: deviceMap[key],
          items: this.buildInspectionItemsForDevice(key),
          showHeatPumpHint: key === 'heat_pump',
        }));
    },
    user_id() {
      return this.$store.getters.getUserId;
    },
    selectedDeviceValues() {
      return this.$store.getters.getSelectedDeviceTechnicalValues;
    },
    oldConsumptionDevicesInspectionData() {
      return this.lastSavedInspectionData;
    },
    supplyInstallationNumberProvided() {
      return this.$store.state.consumptionDevices
        .supply_installation_number_provided;
    },
    isFormValid() {
      return (
        this.areAllDevicesSelected(this.selectedDeviceValues) &&
        this.supplyInstallationNumberProvided
      );
    },
    devicesWithMissingSupplyNumber() {
      const devices =
        this.selectedConsumptionDevicesOrder?.controllable_devices || {};
      const missing = [];
      const supplyRegex = /^60\d{7}$/;
      const deviceLabelMap = {
        wallbox: this.$t('consumption_devices.wallbox'),
        heat_pump: this.$t('consumption_devices.heat_pump'),
        air_conditioning: this.$t('consumption_devices.air_conditioning'),
        battery_storage: this.$t('consumption_devices.battery_storage'),
      };
      Object.entries(devices).forEach(([deviceKey, deviceGroup]) => {
        if (!deviceGroup) return;
        Object.entries(deviceGroup).forEach(([, device]) => {
          const val = device?.supply_installation_number || '';
          if (!val || !supplyRegex.test(val)) {
            missing.push(deviceLabelMap[deviceKey] || deviceKey);
          }
        });
      });
      return missing;
    },
    lastUpdatedComponent() {
      return this.$store.getters.getLastUpdatedComponent;
    },
    notifyEvent() {
      return this.$store.getters.getNotifyEvent
        ? this.$store.getters.getNotifyEvent
        : emptyEventData;
    },
  },
  methods: {
    initializeInspectionValues() {
      this.lastSavedInspectionData = cloneInspectionData(
        this.selectedConsumptionDevicesOrder?.technical_inspection_data
      );
      this.$store.commit('setSelectedDeviceTechnicalValues', {
        orderId: this.selectedConsumptionDevicesOrder?.id || null,
        technicalInspectionData: this.lastSavedInspectionData,
        force: true,
      });
    },
    buildInspectionItemsForDevice(deviceKey) {
      const items = [...baseInspectionItems];
      if (deviceKey === 'battery_storage') {
        items.splice(items.length - 1, 0, storageInspectionItem);
      }

      const deviceValues = this.selectedDeviceValues?.[deviceKey] || {};

      return items.map((item) => {
        const title = this.$t(
          item.heatPumpTitleKey && deviceKey === 'heat_pump'
            ? item.heatPumpTitleKey
            : item.titleKey
        );
        const children = item.children?.map((child) => ({
          ...child,
          title: this.$t(child.titleKey),
          value: deviceValues[child.fieldKey] === true,
        }));

        return {
          ...item,
          title,
          value:
            item.type === 'derived-checkbox'
              ? children.every((child) => child.value === true)
              : deviceValues[item.fieldKey],
          hasError: this.hasInspectionItemError(deviceKey, item.fieldKey),
          children,
          options: item.options?.map((option) => ({
            label: this.$t(option.labelKey),
            value: option.value,
          })),
        };
      });
    },
    hasInspectionItemError(deviceKey, fieldKey) {
      const deviceValues = this.selectedDeviceValues?.[deviceKey] || {};
      if (fieldKey === 'ripple_receiver') {
        return deviceValues[fieldKey] !== 'no';
      }

      if (fieldKey === 'counter_number_checked') {
        return !(
          deviceValues.counter_number_matches_photo_checked === true &&
          deviceValues.counter_number_matches_location_checked === true
        );
      }

      return deviceValues[fieldKey] !== true;
    },
    setInspectionField(deviceKey, fieldKey, value) {
      this.$store.commit('setDeviceInspectionValue', {
        deviceKey,
        fieldKey,
        value,
      });
    },
    onInspectionItemClick(deviceKey, item) {
      if (item.type !== 'checkbox') {
        return;
      }

      this.setInspectionField(deviceKey, item.fieldKey, !item.value);
    },
    toggleSupplyInstallationNumberProvided() {
      this.$store.commit(
        'setSupplyInstallationNumberProvided',
        !this.supplyInstallationNumberProvided
      );
    },
    setSupplyInstallationNumberProvided(value) {
      this.$store.commit('setSupplyInstallationNumberProvided', value);
    },
    confirmConsumptionDevicesInspection() {
      this.alertError = false;
      this.showSupplyNumberMissingError = false;
      const isValid = this.isFormValid;
      const hasMissingSupply = this.devicesWithMissingSupplyNumber.length > 0;
      if (!isValid || hasMissingSupply) {
        if (!isValid) this.showErrors = true;
        if (hasMissingSupply) this.showSupplyNumberMissingError = true;
        return;
      }
      this.showLoading = true;
      this.showErrors = false;
      this.updateConsumptionDevicesInspectionValues(true);
    },
    declineInspection() {
      this.isModalVisible = true;
    },
    closeModal() {
      this.isModalVisible = false;
      this.showDeclineError = false;
      this.noteValue = { note: '' };
      this.showDeclineLoading = false;
    },
    async closeModalAndSaveData() {
      if (this.noteValue.note === '') {
        this.showDeclineError = true;
        return;
      }

      this.showDeclineLoading = true;
      const noteObj = {
        id: this.selectedConsumptionDevicesOrder.id,
        user_id: this.selectedConsumptionDevicesOrder.user_id,
        title: 'Ablehnungsgrund',
        note: this.noteValue.note,
      };

      try {
        await noteService.addConsumptionDeviceNote(noteObj, this.user_id);
        this.$store.commit(
          'setLastUpdatedComponent',
          'consumptionDevicesOrderRefused'
        );
        this.changeStatus(this.noteValue.note);
        this.isModalVisible = false;
        this.noteValue = { note: '' };
        this.showDeclineError = false;
      } finally {
        this.showDeclineLoading = false;
      }
    },
    updateConsumptionDevicesInspectionValues(final) {
      this.alert = false;
      this.alertError = false;
      const currentInspectionData = cloneInspectionData(
        this.selectedDeviceValues
      );
      currentInspectionData.supply_installation_number_provided =
        this.supplyInstallationNumberProvided;
      const changedObject = {
        id: this.selectedConsumptionDevicesOrder.id,
        new_value: { technical_inspection_data: currentInspectionData },
      };
      if (Object.keys(this.oldConsumptionDevicesInspectionData).length > 0) {
        changedObject.old_value = {
          technical_inspection_data: this.oldConsumptionDevicesInspectionData,
        };
      }
      if (final === true) {
        changedObject.status = 'completed';
      }
      this.$store.commit(
        'setLastUpdatedComponent',
        'consumptionDevicesSaveCurrentResult'
      );
      updateConsumptionDeviceTechnicalInspectionData(
        changedObject,
        this.user_id
      )
        .then((res) => {
          this.showLoading = false;
          if (!res || res.error) {
            this.alert = false;
            this.alertError = true;
          } else {
            this.lastSavedInspectionData = currentInspectionData;
            this.$store.commit('updateSelectedConsumptionDevicesData', {
              technical_inspection_data: currentInspectionData,
            });
            if (final === true) {
              this.$store.commit('updateSelectedConsumptionDevicesData', {
                status: 'ORDER_CONFIRMED',
              });
            }
          }
        })
        .catch((err) => {
          this.showLoading = false;
          this.alert = false;
          this.alertError = true;
          console.log(err);
        });
    },
    areAllDevicesSelected(devices) {
      return Object.values(devices).every((device) => {
        const checkboxFields = Object.keys(device).filter(
          (key) => key !== 'ripple_receiver'
        );

        return (
          checkboxFields.every((field) => device[field] === true) &&
          device.ripple_receiver === 'no'
        );
      });
    },
    changeStatus(note) {
      const requestBody = {
        id: this.selectedConsumptionDevicesOrder.id,
        new_value: { status: 'ORDER_DECLINED' },
        old_value: {
          status: this.selectedConsumptionDevicesOrder.status,
        },
        remark: note || '',
      };
      updateConsumptionDevicesOrderStatus(requestBody, this.user_id)
        .then((res) => {
          if (res) {
            this.$store.commit('updateSelectedConsumptionDevicesData', {
              status: 'ORDER_DECLINED',
            });
          } else {
            this.alertError = true;
          }
        })
        .catch((err) => {
          console.log(err);
          this.alertError = true;
        });
    },
  },
  watch: {
    isFormValid(newValue) {
      if (newValue) {
        this.showErrors = false;
      }
    },
    devicesWithMissingSupplyNumber(newValue) {
      if (newValue.length === 0) {
        this.showSupplyNumberMissingError = false;
      }
    },
    notifyEvent(newValue) {
      if (newValue.field === 'updateConsumptionDeviceTechnicalInspectionData') {
        this.alert = true;
      }
      setTimeout(() => {
        this.alert = false;
        this.$store.commit('setNotifyEvent', emptyEventData);
        this.$store.commit('setLastUpdatedComponent', '');
      }, 3000);
    },
    'selectedConsumptionDevicesOrder.id': {
      immediate: true,
      handler(newValue) {
        if (!newValue) {
          return;
        }

        this.initializeInspectionValues();
      },
    },
  },
};
</script>

<style scoped>
.inspection-wrapper {
  display: flex;
  flex-direction: column;
  color: #6e6e6e;
}

.checkbox-groups {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 470px));
  justify-content: flex-start;
  gap: 16px;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 8px 8px 0 0;
}

.checkbox-group {
  width: 100%;
  max-width: 470px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  background: transparent;
}

.group-title {
  font-weight: bold;
  margin-bottom: 12px;
  font-size: 20px;
}

.inspection-item--radio,
.inspection-item--derived-checkbox {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  min-height: auto;
}

.inspection-item--derived-checkbox {
  color: #9e9e9e;
}

.inspection-checkbox--readonly-checked
  ::v-deep
  .v-input--selection-controls__input
  .v-icon {
  color: #9b004b !important;
}

.inspection-checkbox--readonly-checked ::v-deep .v-input--is-disabled .v-icon {
  color: #9b004b !important;
}

.inspection-checkbox--readonly-checked
  ::v-deep
  .v-input--is-disabled
  .theme--light.v-icon {
  color: #9b004b !important;
}

.inspection-item {
  padding: 12px 16px;
  margin-bottom: 12px;
  min-height: 76px;
  display: flex;
  align-items: center;
  background: #ffffff;
  box-shadow: 0px 1px 2px #00000029;
  border-radius: 4px;
  border: thin solid rgba(0, 0, 0, 0.12);
}

.inspection-item--error {
  border-color: #ff5252;
}

.inspection-item--clickable {
  cursor: pointer;
}

.inspection-item-label {
  width: 100%;
  margin-left: 0;
  text-align: left;
  font-size: 20px;
  line-height: 24px;
  color: #6e6e6e;
}
.inspection-subitems {
  width: 100%;
  margin-top: 8px;
  padding-top: 8px;
  border-top: thin solid rgba(0, 0, 0, 0.08);
}

.inspection-checkbox--nested ::v-deep .v-input__slot {
  width: 100%;
  align-items: flex-start;
}

.inspection-checkbox--nested ::v-deep .v-label {
  font-size: 16px;
  line-height: 22px;
}

.inspection-checkbox {
  width: 100%;
}

.inspection-checkbox ::v-deep .v-input__slot {
  margin-bottom: 0;
  width: 100%;
  display: flex;
  flex-direction: row-reverse;
  justify-content: space-between;
  align-items: center;
}

.inspection-checkbox ::v-deep .v-label {
  color: #6e6e6e;
  opacity: 1;
  font-size: 20px;
  line-height: 24px;
  margin-right: auto;
}
.inspection-radio-group {
  margin-left: 0;
  min-height: auto;
  display: flex;
  align-items: flex-start;
  width: 100%;
}

.inspection-radio-group ::v-deep .v-label {
  color: #6e6e6e;
  opacity: 1;
}

.inspection-hint {
  margin-top: 8px;
  font-size: 14px;
  line-height: 18px;
}

.supply-checkbox-row {
  grid-column: 1 / -1;
  padding: 0 16px 16px;
  max-width: 470px;
}

.flex-container {
  display: flex;
  color: #6e6e6e;
}

.flex-container-nested {
  display: flex;
  background-color: rgba(255, 255, 255, 0.9);
  min-height: 56px;
  flex-direction: row;
  padding: 16px;
  justify-content: space-between;
  margin-bottom: 1px;
}

.buttons-label {
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  display: flex;
}

.border-radius-bottom {
  border-radius: 0px 0px 8px 8px;
}

.btn-cancel-inspection {
  background: #ffffff 0% 0% no-repeat padding-box;
  box-shadow: 0px 2px 1px #00000029;
  cursor: pointer;
  padding: 0px;
  border-radius: 8px;
  height: 40px;
  width: 252px;
  border: none;
  opacity: 1;
  margin-left: 30px;
}

.btn-confirm-inspection {
  background: #9b004b 0% 0% no-repeat padding-box;
  box-shadow: 0px 2px 1px #00000029;
  cursor: pointer;
  padding: 0px;
  border-radius: 8px;
  height: 40px;
  width: 252px;
  border: none;
  opacity: 1;
}

.btn-save-current-state {
  border: none;
  font-size: 18px;
  cursor: pointer;
  background: transparent;
  text-decoration: underline;
  color: #6e6e6e !important;
  margin-left: 30px;
}

@media screen and (max-width: 1279px) and (min-width: 1024px) {
  .flex-container-nested {
    display: flex;
    flex-direction: column;
    background-color: rgba(255, 255, 255, 0.9);
    min-height: 56px;
    padding: 16px;
    justify-content: space-between;
    margin-bottom: 1px;
  }
}

@media screen and (max-width: 959px) {
  .checkbox-groups {
    grid-template-columns: minmax(0, 1fr);
  }

  .checkbox-group {
    max-width: none;
  }
  .buttons-label {
    flex-wrap: wrap;
    gap: 12px;
  }

  .btn-cancel-inspection,
  .btn-save-current-state {
    margin-left: 0;
  }
}

.alert_success,
.alert_error {
  display: inline-flex !important;
  margin-top: 0 !important;
  margin-left: 16px;
  align-items: center;
  height: 35px !important;
}
</style>
